// Exam Store Module - 시험 관리
import examService from '@/services/examService'

const state = {
  exams: [],
  currentExam: null,
  examSession: null,
  currentSession: null,
  activeSessions: [],
  sessionStatus: null,
  examStatistics: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0
  },
  monitoringData: {
    students: [],
    examStatus: 'pending',
    startTime: null,
    endTime: null,
    activeStudents: 0,
    submittedStudents: 0
  },
  statistics: null,
  gradingResults: [],
  reports: [],
  loading: false,
  error: null,
  wsConnection: null,
  // 슈퍼 어드민 전용 필드
  globalStatistics: null,
  institutionStats: {},
  allSessions: []
}

const getters = {
  getExams: (state) => state.exams,
  getCurrentExam: (state) => state.currentExam,
  getExamSession: (state) => state.examSession,
  getCurrentSession: (state) => state.currentSession,
  getActiveSessions: (state) => state.activeSessions,
  getSessionStatus: (state) => state.sessionStatus,
  getExamStatistics: (state) => state.examStatistics,
  getPagination: (state) => state.pagination,
  getMonitoringData: (state) => state.monitoringData,
  getStatistics: (state) => state.statistics,
  getGradingResults: (state) => state.gradingResults,
  getReports: (state) => state.reports,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,
  getGlobalStatistics: (state) => state.globalStatistics,
  getInstitutionStats: (state) => state.institutionStats,
  getAllSessions: (state) => state.allSessions,
  getExamsByStatus: (state) => (status) => {
    return state.exams.filter(exam => exam && exam.status === status)
  },
  getActiveExams: (state) => {
    return state.exams.filter(exam => exam && exam.status === 'active')
  },
  getScheduledExams: (state) => {
    return state.exams.filter(exam => exam && exam.status === 'scheduled')
  },
  getCompletedExams: (state) => {
    return state.exams.filter(exam => exam && exam.status === 'completed')
  },
  getExamProgress: (state) => {
    if (!state.monitoringData.students.length) return 0
    return Math.round((state.monitoringData.submittedStudents / state.monitoringData.students.length) * 100)
  }
}

const mutations = {
  SET_EXAMS(state, exams) {
    // Filter out any invalid exam objects
    state.exams = (exams || []).filter(exam => exam && exam.examId)
  },
  SET_CURRENT_EXAM(state, exam) {
    state.currentExam = exam
  },
  ADD_EXAM(state, exam) {
    // Only add valid exam objects with an examId
    if (exam && exam.examId) {
      state.exams.push(exam)
    } else {
      console.warn('Attempted to add invalid exam to state:', exam)
    }
  },
  UPDATE_EXAM(state, updatedExam) {
    const index = state.exams.findIndex(e => e.examId === updatedExam.examId)
    if (index !== -1) {
      state.exams.splice(index, 1, updatedExam)
    }
  },
  DELETE_EXAM(state, examId) {
    state.exams = state.exams.filter(e => e.examId !== examId)
  },
  SET_EXAM_SESSION(state, session) {
    state.examSession = session
  },
  UPDATE_EXAM_SESSION(state, sessionData) {
    state.examSession = { ...state.examSession, ...sessionData }
  },
  SET_MONITORING_DATA(state, data) {
    state.monitoringData = { ...state.monitoringData, ...data }
  },
  UPDATE_STUDENT_STATUS(state, { studentId, status }) {
    const student = state.monitoringData.students.find(s => s.id === studentId)
    if (student) {
      student.status = status
      if (status === 'submitted') {
        state.monitoringData.submittedStudents++
      }
    }
  },
  ADD_STUDENT_TO_MONITORING(state, student) {
    const exists = state.monitoringData.students.find(s => s.id === student.id)
    if (!exists) {
      state.monitoringData.students.push(student)
      state.monitoringData.activeStudents++
    }
  },
  SET_STATISTICS(state, statistics) {
    state.statistics = statistics
  },
  SET_GRADING_RESULTS(state, results) {
    state.gradingResults = results
  },
  ADD_GRADING_RESULT(state, result) {
    state.gradingResults.push(result)
  },
  SET_REPORTS(state, reports) {
    state.reports = reports
  },
  ADD_REPORT(state, report) {
    state.reports.push(report)
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_WS_CONNECTION(state, connection) {
    state.wsConnection = connection
  },
  SET_GLOBAL_STATISTICS(state, statistics) {
    state.globalStatistics = statistics
  },
  SET_INSTITUTION_STATS(state, { institutionId, stats }) {
    state.institutionStats[institutionId] = stats
  },
  SET_ALL_SESSIONS(state, sessions) {
    state.allSessions = sessions
  },
  RESET_MONITORING_DATA(state) {
    state.monitoringData = {
      students: [],
      examStatus: 'pending',
      startTime: null,
      endTime: null,
      activeStudents: 0,
      submittedStudents: 0
    }
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination }
  },
  SET_ACTIVE_SESSIONS(state, sessions) {
    state.activeSessions = sessions || []
  },
  SET_CURRENT_SESSION(state, session) {
    state.currentSession = session
  },
  SET_SESSION_STATUS(state, status) {
    state.sessionStatus = status
  },
  SET_EXAM_STATISTICS(state, statistics) {
    state.examStatistics = statistics
  }
}

const actions = {
  /**
   * 시험 목록 조회 — 모든 페이지를 받아 합친다(examService.fetchAllExams, params 의 page·page_size 는 쓰지 않음)
   * 실패하면 이전 목록을 그대로 두고 throw 하지 않는다
   */
  async fetchExams({ commit }, params = {}) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const exams = await examService.fetchAllExams(params)
      
      commit('SET_EXAMS', exams)
      commit('SET_PAGINATION', { page: 1, limit: exams.length || 20, total: exams.length })
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching exams:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 정보 조회
   */
  async fetchExam({ commit }, examId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const exam = await examService.fetchExam(examId)
      commit('SET_CURRENT_EXAM', exam)
      return exam  // 반환값 추가
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching exam:', error)
      throw error  // 에러도 다시 throw
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 생성
   */
  async createExam({ commit }, examData) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const newExam = await examService.createExam(examData)
      commit('ADD_EXAM', newExam)
      return newExam
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error creating exam:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 수정
   */
  async updateExam({ commit }, { examId, examData }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const updatedExam = await examService.updateExam(examId, examData)
      commit('UPDATE_EXAM', updatedExam)
      return updatedExam
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error updating exam:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 상태 수정
   */
  async updateExamStatus({ commit }, { examId, status }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const updatedExam = await examService.updateExamStatus(examId, status)
      commit('UPDATE_EXAM', updatedExam)
      return updatedExam
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error updating exam status:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 삭제
   */
  async deleteExam({ commit }, examId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      await examService.deleteExam(examId)
      commit('DELETE_EXAM', examId)
      return true
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error deleting exam:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 복사 — 목록 새로고침은 부르는 쪽이 본인 creator_email 로 한다(ExamList confirmCopyExam)
   * 여기서 파라미터 없이 다시 받으면 백엔드 기본값 10건으로 목록이 바뀌고, 새로고침 실패가 복사 실패로 보인다
   */
  async copyExam({ commit }, { sourceExamId, newExamName }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      return await examService.copyExam(sourceExamId, newExamName)
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error copying exam:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 공유
   */
  async shareExam({ commit }, shareData) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const shareResult = await examService.shareExam(shareData)
      return shareResult
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error sharing exam:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 공유 설정 조회
   */
  async getShareSettings({ commit }, examId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const shareSettings = await examService.getShareSettings(examId)
      return shareSettings
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching share settings:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 공유 설정 수정
   */
  async updateShareSettings({ commit }, { examId, shareData }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const result = await examService.updateShareSettings(examId, shareData)
      return result
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error updating share settings:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 공유 취소
   */
  async revokeShare({ commit }, examId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const result = await examService.revokeShare(examId)
      return result
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error revoking share:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 공유 시험 목록 조회 (Deprecated - fetchSharedExamsToMe 사용 권장)
   */
  async fetchSharedExams({ commit }, params) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const sharedExams = await examService.fetchSharedExams(params)
      return sharedExams
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching shared exams:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 나에게 공유된 시험 목록 조회 (지정 공유 방식)
   */
  async fetchSharedExamsToMe({ commit }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const sharedExams = await examService.fetchSharedExamsToMe()
      return sharedExams
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching shared exams to me:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 공유 대상자 수정
   */
  async updateShareRecipients({ commit }, { shareId, recipients }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const result = await examService.updateShareRecipients(shareId, recipients)
      return result
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error updating share recipients:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 공유 시험 접근
   */
  async accessSharedExam({ commit }, shareCode) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const sharedExam = await examService.accessSharedExam(shareCode)
      return sharedExam
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error accessing shared exam:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험에 연결된 클래스 정보 조회
   */
  async fetchExamClassroom({ commit }, examId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const classroom = await examService.fetchExamClassroom(examId)
      return classroom
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching exam classroom:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 응시 학생 목록 조회
   */
  async fetchExamStudents({ commit }, { classroomIdx, examId }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const students = await examService.fetchExamStudents(classroomIdx, examId)
      commit('SET_MONITORING_DATA', { students: students || [] })
      return students
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching exam students:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 응시 학생 목록 갱신
   */
  async updateExamStudents({ commit }, { classroomIdx, examId, studentIds }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      return await examService.updateExamStudents(classroomIdx, examId, studentIds)
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error updating exam students:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 출석 정보 조회
   */
  async fetchAttendance({ commit }, examId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const attendance = await examService.fetchAttendance(examId)
      return attendance
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching attendance:', error)
      return []
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 출석 정보 저장
   */
  async saveAttendance({ commit }, attendanceData) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const result = await examService.saveAttendance(attendanceData)
      return result
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error saving attendance:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 출석 정보 초기화
   */
  async clearAttendance({ commit }, { examId, classroomIdx }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const result = await examService.clearAttendance(examId, classroomIdx)
      return result
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error clearing attendance:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 시작
   */
  async startExam({ commit }, { examId, classroomIdx }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      // 대면 시험 수동 시작 신호 전송 (푸시 등 부가 작업 포함)
      const exam = await examService.manualStartSignal(examId, classroomIdx)
      commit('UPDATE_EXAM', exam)
      commit('UPDATE_EXAM_SESSION', {
        status: 'started',
        startedAt: new Date().toISOString()
      })
      return exam
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error starting exam:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 리포트 생성
   */
  async generateReport({ commit }, examId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const report = await examService.generateReport(examId)
      return report
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error generating report:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 리포트 조회
   */
  async fetchExamReport({ commit }, examId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const statistics = await examService.fetchExamReport(examId)
      commit('SET_EXAM_STATISTICS', statistics)
      return statistics
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching exam report:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 시험 리포트 다운로드
   */
  async downloadReport(examId) {
    try {
      return await examService.downloadReport(examId)
    } catch (error) {
      console.error('Error downloading report:', error)
      throw error
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}